import { useAppDispatch, useAppSelector } from "../../app";
import selectAnnotationReducer from "../../annotation/selectors/annotationReducerSelector";
import { useCallback, useEffect, useRef } from "react";
import { mapToEntityAnnotationDto } from "../../annotation/utils/annotationMappers";
import { createHttpClient } from "../../common/utilities/HttpModule";

import {
  EntityAnnotationBatchToBeNormalizedDto,
  EntityAnnotationNormalizedDto,
  NormalizationErrorType,
} from "../../annotation/interfaces/annotation";
import { NormalizedOutput } from "../interfaces/normalization";
import {
  addOrUpdateAnnotation,
  updateAnnotationNormalizationInState,
} from "../../annotation/store/annotationSlice";
import useAnnotationLoading from "../../annotation/hooks/useAnnotationLoading";
// @ts-expect-error object-hash ships no type declarations and there is no
// @types/object-hash installed.
import hash from "object-hash";
import useCategorizationDataFetcher from "../../metadata/hooks/useCategorizationDataFetcher";
import { getCategorizationAnnotationForEntityAnnotation } from "../utils/entityCategorizationUtils";
import {
  CUSTOMER_NORMALIZATION_STATUSES,
  CUSTOMER_NORMALIZED_ENTITY_IDS,
  customerNormalizationInputs,
} from "../utils/customerNormalization";

const http = createHttpClient("CUSTOMER_NORMALIZATION_API");

const orgsToTrigger = ["topsfoods", "ardo", "ontex", "klassifai"];
const updateNormalizedEntities = [
  "topfoodsCartonsToUnits",
  "topsfoodsQuantyNormalized",
  "ardoQuantity",
  "ardoQuantityUnit",
  "ontexQuantityUnit",
];

const useAllAnnotationNormalizations = () => {
  // @ts-expect-error `orgToTest` is a manual debugging override set on window
  // from the browser console; it is not part of the app's own global surface.
  const testOrg: string = window.orgToTest || "";

  const prevHashAnnotations = useRef("");
  const dispatch = useAppDispatch();
  const { setLoading } = useAnnotationLoading();

  const status = useAppSelector(
    (state) => state.documentSetsReducer.activeDocumentSet?.status,
  );

  const documentId = useAppSelector(
    (state) => state.documentReducer.activeDocument?.id,
  );

  const allAnnotations = useAppSelector(selectAnnotationReducer);
  const metaData = useAppSelector(
    (state) => state.documentReducer.activeDocument?.metaData,
  );

  const org = useAppSelector(
    (state) => state.userReducer.user?.organization.name,
  );

  const configMap = useAppSelector((state) => state.configMapReducer.configMap);

  // Subscribed to on purpose: the categorizations are part of the normalization
  // input (a quantity pointing at cases instead of pieces changes the
  // palletisation), but `fetchCategorizationAnnotationData` reads them from a
  // ref, so without this selector a categorization change never re-runs the
  // effect and the normalization is never asked for again.
  const categorizationAnnotationsInState = useAppSelector(
    (state) =>
      state.metadataReducer.categorizationState.categorizationAnnotations,
  );

  const { fetchCategorizationAnnotationData } =
    useCategorizationDataFetcher(false);

  const runNormalizeUsingAllAnnotations = useCallback(async () => {
    const categorizationAnnotations = await fetchCategorizationAnnotationData(
      categorizationAnnotationsInState,
    );

    if (!status || !CUSTOMER_NORMALIZATION_STATUSES.includes(status)) {
      return;
    }

    if (!org) {
      return;
    }

    if (!orgsToTrigger.includes(org?.toLowerCase())) {
      return;
    }

    if (org === "klassifai" && !testOrg) {
      return;
    }

    if (!allAnnotations?.length) {
      return;
    }

    if (!metaData) {
      return;
    }

    // Only hash the inputs of the customer normalizations, never what they answer.
    // This prevents re-triggering when the POST response updates annotations with normalization data.
    //
    // Hash membership must not depend on the API response either: excluding annotations by a
    // `normalizedMethod` the response sets made a response that toggled an annotation's method
    // (e.g. a list-method one round, "isError" the next for a non-idempotent customer) flip it
    // in/out of the hash and re-trigger the POST endlessly. See `customerNormalizationInputs`.
    const annotationsForHashing = customerNormalizationInputs(allAnnotations);

    const categorizationsForHashing = (categorizationAnnotations || []).map(
      (c) => ({
        categorizationId: c.categorizationId,
        externalDataId: c.externalDataId,
        groupBlockId: c.groupBlockId,
        index: c.index,
        value: c.value,
        visibleValue: c.visibleValue,
      }),
    );

    const annotationsHash = hash({
      annotations: annotationsForHashing,
      categorizations: categorizationsForHashing,
    });

    if (prevHashAnnotations.current === annotationsHash) {
      return;
    }

    prevHashAnnotations.current = annotationsHash;

    const annotationsDto = [...allAnnotations].map((annotation) => {
      const normalized: EntityAnnotationNormalizedDto | undefined =
        annotation.entityAnnotationNormalization
          ? {
              value: annotation.entityAnnotationNormalization.normalizedValue,
              method:
                annotation.entityAnnotationNormalization.normalizedMethod || "",
            }
          : undefined;

      const categorizationAnnotation =
        getCategorizationAnnotationForEntityAnnotation(
          configMap,
          annotation.entity.id,
          annotation.index || 1,
          categorizationAnnotations,
        );

      return mapToEntityAnnotationDto(
        annotation.id,
        annotation.values.join(" "),
        annotation.index || 1,
        annotation.entity.entityNormalizations ?? [],
        annotation.entity.id,
        annotation.multipleGroupBlocks,
        categorizationAnnotation, // TODO Check maarten if this needs changes
        undefined,
        normalized,
      );
    });

    const categorizationAnnotationsDto = (categorizationAnnotations || []).map(
      (c) => ({
        categorizationId: c.categorizationId,
        value: c.value,
        visibleValue: c.visibleValue,
        index: c.index,
      }),
    );

    const entityAnnotationsBody: EntityAnnotationBatchToBeNormalizedDto = {
      entityAnnotations: annotationsDto,
      categorizationAnnotations: categorizationAnnotationsDto,
      metaData: metaData,
    };

    const orgToCall = org === "klassifai" ? testOrg : org;
    const url = `/customer/normalization/api/v1/search/${orgToCall.toLowerCase()}`;

    const loadingAnnotationIds = allAnnotations
      .filter((annotation) =>
        CUSTOMER_NORMALIZED_ENTITY_IDS.includes(annotation.entity.id),
      )
      .map((annotation) => annotation.id);

    try {
      setLoading(loadingAnnotationIds, true);

      const { data } = await http.post<Array<NormalizedOutput>>(
        url,
        entityAnnotationsBody,
      );

      data.forEach((d) => {
        const annotation = allAnnotations.find((a) => a.id === d.id!);
        if (d.normalizedMethod! === "isError" && annotation) {
          dispatch(
            updateAnnotationNormalizationInState({
              id: d.id!,
              newValue: d.normalizedValue,
              method: undefined,
              multipleGroupBlocks: annotation.multipleGroupBlocks,
              isByUser: true,
              errorType: NormalizationErrorType.ERROR_WHILE_NORMALIZING,
            }),
          );
        } else if (
          updateNormalizedEntities.includes(d.normalizedMethod!) &&
          annotation
        ) {
          dispatch(
            updateAnnotationNormalizationInState({
              id: d.id!,
              method: d.normalizedMethod,
              newValue: d.normalizedValue,
              multipleGroupBlocks: annotation.multipleGroupBlocks,
            }),
          );
        } else {
          const isMultipleGroupBlocks = CUSTOMER_NORMALIZED_ENTITY_IDS.includes(
            d.id!,
          );
          const index = d.index || 1;
          // Here `d.id` is the entity type of a computed output, not an
          // annotation id. Its annotation carries the result in its
          // normalization and keeps `values` empty, and `addOrUpdateAnnotation`
          // only ever writes `values` — so an output that already exists has to
          // be updated through its normalization or a recalculated value (a
          // palletisation following a changed quantity) would be dropped.
          const existingOutput = allAnnotations.find(
            (a) => a.entity.id === d.id && (a.index || 1) === index,
          );

          if (existingOutput) {
            dispatch(
              updateAnnotationNormalizationInState({
                id: existingOutput.id,
                method: d.normalizedMethod,
                newValue: d.normalizedValue,
                multipleGroupBlocks: existingOutput.multipleGroupBlocks,
              }),
            );
            return;
          }

          dispatch(
            addOrUpdateAnnotation({
              annotation: {
                documentId: documentId!,
                id: `temp_${crypto.randomUUID()}`,
                page: 1,
                values: [],
                pageTokenIndices: [],
                index,
                isOutput: true,
                multipleGroupBlocks: isMultipleGroupBlocks,
                entityAnnotationNormalization: {
                  normalizedValue: d.normalizedValue,
                  normalizedMethod: d.normalizedMethod!,
                },
                entity: {
                  id: d.id!,
                  name: "",
                  color: "",
                  entityType: "NER",
                  multipleGroupBlocks: isMultipleGroupBlocks,
                },
                isByUser: false,
                modelScore: 100,
              },
              documentId: documentId!,
              multipleGroupBlocks: isMultipleGroupBlocks,
            }),
          );
        }
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(loadingAnnotationIds, false);
    }
  }, [
    status,
    org,
    allAnnotations,
    metaData,
    dispatch,
    documentId,
    configMap,
    testOrg,
    categorizationAnnotationsInState,
    fetchCategorizationAnnotationData,
    setLoading,
  ]);

  useEffect(() => {
    runNormalizeUsingAllAnnotations();
  }, [runNormalizeUsingAllAnnotations]);
};

export default useAllAnnotationNormalizations;
